import React, { ReactNode } from 'react';

import cls from 'classnames';

import styles from './RadioGroup.scss';

type RadioValue = string | boolean | number | null;

export type RadioGroupItem<T = RadioValue> = {
  /** Значение айтема, будет возвращаться в onChange */
  value: T;
  /** Текст который будет отображаться в радио инпуте */
  label: ReactNode;
  /** Класснейм айтема радио группы */
  className?: string;
  /** Нужно ли дизейблить данный айтем, опционально */
  isDisabled?: boolean;
  /** Описание для радио инпута, опционально */
  description?: ReactNode;
  /* Передаем data-locator для тестирования  */
  specificLocators?: string | string[];
  /* Передаем стили для лейбла */
  labelStyle?: React.CSSProperties;
  /* Выделить ли лейбл как невалидный */
  isInvalid?: boolean;
};

type Props<T> = {
  /** Имя которое будет использоваться для радио группы */
  name: string;
  /** Передаем свои классы для управления стилями элементов лейбла, описания и инпута, опционально */
  childrenClassNames?: {
    input?: string;
    label?: string;
    description?: string;
    bullet?: string;
    radio?: string;
    labelText?: string;
  };
  /** Передаем свой класс для управления стилем самого компонента, опционально */
  className?: string;
  /** Передаем значение текущего активного айтема, для того чтобы отрисовать его выбранным */
  value: T;
  /** Колбэк срабатывающий при выборе какого-либо айтема радио группы, возвращающий значение айтема */
  onChange: (value: T) => void;
  /** Массив айтемов для отрисовки в радио группе */
  items: RadioGroupItem<T>[];
};

const RadioGroup = <T extends RadioValue>(props: Props<T>) => {
  const {
    name: groupName,
    value: currentValue,
    items,
    onChange,
    childrenClassNames = {},
    className,
  } = props;

  return (
    <div className={cls(styles.RadioGroup, className)} data-locator={`radio-group-${groupName}`}>
      {items.map((item) => {
        const {
          label,
          labelStyle,
          value,
          isDisabled,
          description,
          specificLocators = [],
          isInvalid,
        } = item;
        const isChecked = value === currentValue;

        const stringValue = String(value);
        const id = `${groupName}-${stringValue}`;

        return (
          <label
            key={id}
            data-locator={cls(`radio-group-${groupName}-${stringValue}`, specificLocators)}
            className={cls(styles.row, childrenClassNames.label, item.className, {
              [styles.rowIsInvalid]: isInvalid,
              [styles.rowIsChecked]: isChecked,
              [styles.rowIsDisabled]: isDisabled,
            })}
            htmlFor={id}
            aria-disabled={Boolean(isDisabled)}
            aria-checked={isChecked}
            style={labelStyle}
          >
            <div
              className={cls(styles.bullet, childrenClassNames.bullet)}
              data-locator="radio-bullet"
            >
              <input
                id={id}
                className={cls(styles.input, childrenClassNames.input)}
                type="radio"
                value={stringValue}
                name={groupName}
                checked={isChecked}
                disabled={isDisabled}
                onChange={() => onChange(value)}
              />
              <div className={cls(styles.radio, childrenClassNames.radio)} />
            </div>
            <div className={cls(styles.label, childrenClassNames.labelText)}>
              {label}
              {description && (
                <div className={cls(styles.description, childrenClassNames.description)}>
                  {description}
                </div>
              )}
            </div>
          </label>
        );
      })}
    </div>
  );
};

const genericMemo: <T>(component: T) => T = React.memo;
export default genericMemo(RadioGroup);
